import BackArrow from "@/ui/Icons/BackArrow";
import { useRouter } from "next/router";
import {
  AdditionalWidgetProperties,
  Configure,
  InstantSearch,
  SearchBox,
  useInfiniteHits,
  useRefinementList,
  UseRefinementListProps,
} from "react-instantsearch";
import { FC, useEffect, useState } from "react";
import SearchIcon from "@/ui/Icons/SearchIcon";
import Hat from "@/ui/Icons/Hat";
import BowlIcon from "@/ui/Icons/BowlIcon";
import { useSupabaseClient, useUser } from "@supabase/auth-helpers-react";
import Switch from "@/ui/Icons/Switch";
import { toast } from "react-hot-toast";
import Image from "next/image";
import emojis from "@/ui/emoji.json";
import InfiniteScroll from "react-infinite-scroll-component";
import Loader from "@/components/Loaders/Loader";
import algoliasearch from "algoliasearch";
import { de } from "date-fns/locale";
import { idAndNameToSlug } from "@/lib/slug";

const searchClient = algoliasearch(
  process.env.NEXT_PUBLIC_ALGOLIA_APP_ID ?? "",
  process.env.NEXT_PUBLIC_ALGOLIA_API_KEY ?? ""
);
export default function SearchScreen(props: any) {
  return (
    <InstantSearch
      searchClient={searchClient}
      indexName={process.env.NEXT_PUBLIC_ALGOLIA_RECIPE_INDEX}
      insights={true}
    >
      <Search />
    </InstantSearch>
  );
}

function Search(props: any) {
  const router = useRouter();
  const user = useUser();
  const [selectedItem, setSelectedItem] = useState<any>();
  const [calendar, setCalendar] = useState<any>();
  const supabaseClient = useSupabaseClient();
  const isReplace = router?.query?.replace == "true" ? true : false;
  const { hits, isLastPage, showMore } = useInfiniteHits(props);

  const replace = async () => {
    const { data, error } = await supabaseClient
      .from("calendar")
      .update({
        old_recipe_id: calendar?.recipe?.id,
        recipe_id: selectedItem?.id,
        skipped: false,
      })
      .eq("id", calendar?.id);
    if (error) {
      console.log(error);
    } else {
      // store recipe feedback
      await supabaseClient.from("recipe_feedback").insert([
        {
          recipe_id: calendar?.recipe?.id,
          swipe_rating: false,
          user_id: user?.id,
        },
        {
          recipe_id: selectedItem?.id,
          swipe_rating: true,
          user_id: user?.id,
        },
      ]);
      router.back();
      toast.success("Recipe replaced!");
    }
  };
  useEffect(() => {
    const fetchCalendar = async () => {
      const { data, error } = await supabaseClient
        .from("calendar")
        .select(
          `*, 
            recipe!calendar_recipe_id_fkey(
            id,
            name,
            imageURL,
            servings:serving(
               id,
               cookTime,
               prepTime                                                                                                                                                            
              )
            )`
        )
        .eq("id", router?.query?.calendarId)
        .limit(1)
        .single();
      if (error) console.log("error", error);
      else setCalendar(data);
    };
    if (router?.query?.calendarId && user) {
      fetchCalendar();
    }
  }, [router?.query?.calendarId]);

  return (
    <section
      className={"bg-[#F9F7F7] h-screen w-screen overflow-scroll"}
      id={"scrollableDiv"}
    >
      <div className="max-w-xl mx-auto">
        <nav className="flex items-center justify-center bg-white px-[30px] py-[13px] sticky top-0 rounded-b-2xl">
          <button onClick={() => router.back()} className={"absolute left-6"}>
            <BackArrow />
          </button>
          <div className={"flex align-middle"}>
            <p className={"px-[10px] font-medium"}>
              Search {isReplace && "& Replace"}
            </p>
          </div>
        </nav>
        <section className="px-4">
          <div className="flex gap-x-2 items-center mt-7 mb-4 rounded-xl p-1 px-4 bg-white">
            <SearchIcon />
            <SearchBox
              placeholder="Search for recipes"
              classNames={{
                root: "w-full",
                input:
                  "rounded-[5px] bg-transparent border-none p-2 rounded-lg focus:outline-none w-full border-2 border-gray-400",
                reset: "hidden",
                submit: "hidden",
                loadingIndicator: "hidden",
              }}
              title="Search for recipes"
            />
          </div>
          <div className="py-2 flex gap-x-2 overflow-x-auto no-scrollbar">
            <DietTags attribute={"dietTags"} />
          </div>
          <div className="mt-2">
            {calendar ? (
              <Configure
                analytics={true}
                filters={`NOT objectID:${calendar?.recipe_id}`}
              />
            ) : (
              ""
            )}
            <InfiniteScroll
              style={{ overflow: "visible" }}
              dataLength={hits?.length}
              next={showMore}
              hasMore={!isLastPage}
              scrollableTarget={"scrollableDiv"}
              loader={
                <div className={"flex justify-center py-4"}>
                  <Loader size={4} />
                </div>
              }
              endMessage={
                <p className={"text-center py-4"}>End of the results</p>
              }
            >
              <div className={"flex flex-col gap-y-5"}>
                {hits?.map((hit, index) => {
                  return (
                    <RecipeHit
                      hit={hit}
                      key={index}
                      onClick={(p: any) =>
                        isReplace
                          ? setSelectedItem(p)
                          : router.push(
                              `/${idAndNameToSlug(hit?.id, hit?.name)}`
                            )
                      }
                      selectedItem={selectedItem}
                    />
                  );
                })}
              </div>
            </InfiniteScroll>
          </div>
        </section>
        {isReplace ? (
          <ReplaceItemBox
            calendar={calendar}
            selectedItem={selectedItem}
            replace={replace}
          />
        ) : (
          ""
        )}
      </div>
    </section>
  );
}

const RecipeHit: FC<any> = ({ hit, onClick, selectedItem }) => {
  return (
    <div
      onClick={() => {
        onClick(hit);
      }}
    >
      <div
        className={`flex rounded-2xl p-2 gap-x-4 px-4 duration-400 ease-in transition-all ${
          selectedItem?.id === hit?.id
            ? "border-[#15BC6E] bg-[#E3F5EA]  border cursor-pointer"
            : "bg-white"
        }`}
      >
        {/*image container*/}
        <div className="w-20 h-20 rounded-full overflow-hidden shrink-0">
          <Image
            height={80}
            width={80}
            src={hit?.imageURL}
            alt={hit?.name}
            className="w-[80px] h-[80px] object-cover"
          />
        </div>
        {/*info container*/}
        <div className="py-2">
          <h2 className="font-semibold text-base text-[#343233]">
            {hit?.name}
          </h2>
          <div className="flex items-center">
            <span className="font-normal text-[#343233] text-xs flex items-center gap-x-2">
              <Hat className="w-4 h-4" />
              {hit?.prepTime} mins
            </span>
            &nbsp; | &nbsp;
            <span className="font-normal text-[#343233] text-xs flex items-center gap-x-2">
              <BowlIcon />
              {hit?.cookTime} mins
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

const ReplaceItemBox: FC<any> = ({ calendar, selectedItem, replace }) => {
  const [imgSrc, setImgSrc] = useState<string>(selectedItem?.imageURL);

  useEffect(() => {
    setImgSrc(selectedItem?.imageURL);
  }, [selectedItem?.imageURL]);

  return (
    <nav className="fixed bottom-0 bg-white w-full max-w-xl">
      <h4 className="bg-[#BAE7CB] py-1 px-4 font-medium text-sm rounded-t-2xl">
        Replace meal
      </h4>
      <div className={"flex px-2 py-3 sm:pb-4 pb-8 justify-between"}>
        <div className="flex gap-x-2 w-full">
          {/*    old item */}
          <div className="flex flex-col items-center px-2">
            <Image
              height={50}
              width={50}
              src={calendar?.recipe?.imageURL}
              alt={calendar?.recipe?.name}
              className={
                "w-[50px] h-[50px] bg-gray-400 rounded-full overflow-hidden object-cover"
              }
            />
            {/* <h4 className="mt-2">{calendar?.recipe?.name}</h4> */}
            <h4 className="mt-2 text-xs font-medium truncate line-clamp-2 max-w-[100px] whitespace-pre-wrap text-center">
              {calendar?.recipe?.name}
            </h4>
          </div>
          {/*    new item */}
          <div className="grid place-items-center">
            <Switch color={"#007D32"} className="w-5 h-5" />
          </div>
          <div className="flex flex-col items-center px-2">
            <div className="w-[50px] h-[50px] rounded-full bg-slate-200">
              {selectedItem?.imageURL && (
                <Image
                  onError={() => {
                    setImgSrc("/gray.png");
                  }}
                  height={50}
                  width={50}
                  src={imgSrc ? imgSrc : "/gray.png"}
                  alt={selectedItem?.name}
                  className={
                    "w-[50px] h-[50px] rounded-full overflow-hidden object-cover bg-slate-200"
                  }
                />
              )}
            </div>
            <h4 className="mt-2 text-xs font-medium truncate line-clamp-2 whitespace-pre-wrap text-center">
              {selectedItem ? selectedItem?.name : "Choose Meal"}
            </h4>
          </div>
          {/*    replace button */}
        </div>
        <div className="self-center mr-2">
          <button
            onClick={replace}
            className={"bg-green-600 p-2 text-white rounded-lg"}
          >
            Replace
          </button>
        </div>
      </div>
    </nav>
  );
};

const DietTags: FC<any> = (
  props: UseRefinementListProps,
  additionalWidgetProperties?: AdditionalWidgetProperties
) => {
  const { items, refine } = useRefinementList(
    props,
    additionalWidgetProperties
  );
  const [defaultFilter, setDefaultFilter] = useState<any>(null);
  const router = useRouter();

  useEffect(() => {
    setDefaultFilter(router?.query?.filter);
  }, [router?.query?.filter]);

  useEffect(() => {
    if (defaultFilter) {
      refine(defaultFilter);
    }
  }, [defaultFilter]);
  return (
    <>
      {items?.map((item, index) => (
        <DietChip
          key={index}
          text={item?.label}
          onClick={() => refine(item?.value as string)}
          selected={item?.isRefined}
        />
      ))}
    </>
  );
};

const DietChip: FC<any> = ({ text = "", onClick, selected = false }) => {
  return (
    <button
      onClick={onClick}
      className={`flex px-[10px] py-[5px] border-[0.5px] border-[#15BC6E] bg-[${
        selected ? "#15bc6e1a" : "#15bc6e1d"
      }] rounded-[8px] shrink-0`}
    >
      {/* emoji*/}
      {/*text*/}
      <p className={`text-[#005E1E] font-medium text-[14px]`}>
        {/* @ts-ignore */}
        {emojis?.[text]}
        &nbsp;
        {text}
      </p>
    </button>
  );
};
